/**
 * Who may appear in the Tasks inbox provider filter, and whose tasks may be listed.
 * AccessPolicy can still return a broader Task search; the inbox applies this matrix
 * before rendering. See docs/users/PRD_RBAC_FHIR_MAPPING.md.
 */
import type { UnifyRole } from '@unify/fhir-model/resources/practitioner';

export interface TaskInboxCandidate {
  id: string;
  reference: string;
  role?: UnifyRole;
  organizationId?: string;
  /** Practitioner ids recorded as this person's supervisor. */
  supervisorIds: string[];
}

const PRACTICE_ROLES = new Set<UnifyRole>([
  'practice-owner',
  'practice-admin',
  'practice-assistant',
  'supervisor',
  'provider',
]);

export function taskInboxCandidatesForViewer(args: {
  viewerRole: UnifyRole | undefined;
  viewerId: string | undefined;
  viewerOrganizationId: string | undefined;
  candidates: TaskInboxCandidate[];
}): TaskInboxCandidate[] {
  const { viewerRole, viewerId, viewerOrganizationId, candidates } = args;
  const self = candidates.filter((candidate) => candidate.id === viewerId);

  if (!viewerRole || !viewerId) return self;

  if (viewerRole === 'unify-admin') return candidates;
  if (viewerRole === 'unify-assistant') {
    return candidates.filter((candidate) => candidate.role !== 'unify-admin');
  }
  if (viewerRole === 'provider') return self;
  if (viewerRole === 'supervisor') {
    return candidates.filter(
      (candidate) => candidate.id === viewerId || candidate.supervisorIds.includes(viewerId),
    );
  }

  if (!PRACTICE_ROLES.has(viewerRole) || !viewerOrganizationId) return self;
  const excluded = new Set<UnifyRole>();
  if (viewerRole === 'practice-admin') excluded.add('practice-owner');
  if (viewerRole === 'practice-assistant') {
    excluded.add('practice-owner');
    excluded.add('practice-admin');
  }
  return candidates.filter((candidate) => {
    if (candidate.id === viewerId) return true;
    if (candidate.organizationId !== viewerOrganizationId) return false;
    return candidate.role == null || !excluded.has(candidate.role);
  });
}

export interface TaskInboxOwnerScope {
  allowedOwnerRefs: ReadonlySet<string>;
  /** Unify staff see Tasks owned by any Organization (e.g. Organization/unify claim-error Tasks). */
  anyOrganizationOwner: boolean;
}

/**
 * Owners whose Tasks the viewer may list: the visible practitioners plus Organization owners.
 * Practice owner/admin/assistant add their own Organization; unify-admin/assistant see any Organization.
 * Provider and supervisor return before the practice branch in `taskInboxCandidatesForViewer`, so they get no
 * Organization owner here either.
 */
export function taskInboxOwnerScope(args: {
  viewerRole: UnifyRole | undefined;
  viewerOrganizationId: string | undefined;
  practitionerRefs: Iterable<string>;
}): TaskInboxOwnerScope {
  const { viewerRole, viewerOrganizationId, practitionerRefs } = args;
  const allowedOwnerRefs = new Set(practitionerRefs);
  const anyOrganizationOwner = viewerRole === 'unify-admin' || viewerRole === 'unify-assistant';
  const practiceRole = viewerRole != null
    && PRACTICE_ROLES.has(viewerRole)
    && viewerRole !== 'provider'
    && viewerRole !== 'supervisor';
  if (practiceRole && viewerOrganizationId) allowedOwnerRefs.add(`Organization/${viewerOrganizationId}`);
  return { allowedOwnerRefs, anyOrganizationOwner };
}

/** Organization-owned Tasks match `team` (never `mine`) and an explicit Organization choice. */
export function taskMatchesInboxOwnerFilter(
  ownerRef: string | undefined,
  selected: string[],
  viewerRef: string | undefined,
  allowedOwnerRefs: ReadonlySet<string>,
  anyOrganizationOwner = false,
): boolean {
  const ownerAllowed = (ref: string): boolean =>
    allowedOwnerRefs.has(ref) || (anyOrganizationOwner && ref.startsWith('Organization/'));
  if (!ownerRef || !ownerAllowed(ownerRef) || selected.length === 0) return false;
  return selected.some((choice) => {
    if (choice === 'mine') return viewerRef != null && ownerRef === viewerRef;
    if (choice === 'team') return viewerRef != null && ownerRef !== viewerRef;
    return ownerAllowed(choice) && ownerRef === choice;
  });
}
